/**
 * AXOIX Language Configuration
 * 81 Languages: 22 Indian + 59 Global
 * Matching AXOIX voice control language support
 */

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  region: 'indian' | 'global';
  currency: string;
  flag: string;
  rtl?: boolean; // Right-to-left languages
}

// ===============================
// 22 INDIAN LANGUAGES
// ===============================
export const indianLanguages: Language[] = [
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'mai', name: 'Maithili', nativeName: 'मैथिली', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'sa', name: 'Sanskrit', nativeName: 'संस्कृतम्', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', region: 'indian', currency: 'INR', flag: '🇮🇳', rtl: true },
  { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', region: 'indian', currency: 'INR', flag: '🇮🇳', rtl: true },
  { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', region: 'indian', currency: 'INR', flag: '🇳🇵' },
  { code: 'kok', name: 'Konkani', nativeName: 'कोंकणी', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'doi', name: 'Dogri', nativeName: 'डोगरी', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'ks', name: 'Kashmiri', nativeName: 'कॉशुर', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'mni', name: 'Manipuri', nativeName: 'মৈতৈলোন্', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'brx', name: 'Bodo', nativeName: 'बड़ो', region: 'indian', currency: 'INR', flag: '🇮🇳' },
  { code: 'sat', name: 'Santali', nativeName: 'ᱥᱟᱱᱛᱟᱲᱤ', region: 'indian', currency: 'INR', flag: '🇮🇳' },
];

// ===============================
// 59 GLOBAL LANGUAGES
// ===============================
export const globalLanguages: Language[] = [
  // Major World Languages
  { code: 'en', name: 'English', nativeName: 'English', region: 'global', currency: 'USD', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', region: 'global', currency: 'EUR', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', region: 'global', currency: 'EUR', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', region: 'global', currency: 'EUR', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', region: 'global', currency: 'EUR', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', region: 'global', currency: 'BRL', flag: '🇧🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', region: 'global', currency: 'RUB', flag: '🇷🇺' },

  // East Asian
  { code: 'ja', name: 'Japanese', nativeName: '日本語', region: 'global', currency: 'JPY', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', region: 'global', currency: 'KRW', flag: '🇰🇷' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', region: 'global', currency: 'CNY', flag: '🇨🇳' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', region: 'global', currency: 'TWD', flag: '🇹🇼' },

  // Middle East & North Africa
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', region: 'global', currency: 'AED', flag: '🇦🇪', rtl: true },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', region: 'global', currency: 'ILS', flag: '🇮🇱', rtl: true },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', region: 'global', currency: 'IRR', flag: '🇮🇷', rtl: true },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', region: 'global', currency: 'TRY', flag: '🇹🇷' },

  // Southeast Asian
  { code: 'th', name: 'Thai', nativeName: 'ไทย', region: 'global', currency: 'THB', flag: '🇹🇭' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', region: 'global', currency: 'VND', flag: '🇻🇳' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', region: 'global', currency: 'IDR', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', region: 'global', currency: 'MYR', flag: '🇲🇾' },
  { code: 'fil', name: 'Filipino', nativeName: 'Filipino', region: 'global', currency: 'PHP', flag: '🇵🇭' },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာဘာသာ', region: 'global', currency: 'MMK', flag: '🇲🇲' },
  { code: 'km', name: 'Khmer', nativeName: 'ភាសាខ្មែរ', region: 'global', currency: 'KHR', flag: '🇰🇭' },

  // European
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', region: 'global', currency: 'EUR', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', region: 'global', currency: 'PLN', flag: '🇵🇱' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', region: 'global', currency: 'SEK', flag: '🇸🇪' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', region: 'global', currency: 'NOK', flag: '🇳🇴' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', region: 'global', currency: 'DKK', flag: '🇩🇰' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', region: 'global', currency: 'EUR', flag: '🇫🇮' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', region: 'global', currency: 'EUR', flag: '🇬🇷' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', region: 'global', currency: 'CZK', flag: '🇨🇿' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', region: 'global', currency: 'RON', flag: '🇷🇴' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', region: 'global', currency: 'HUF', flag: '🇭🇺' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', region: 'global', currency: 'UAH', flag: '🇺🇦' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', region: 'global', currency: 'BGN', flag: '🇧🇬' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', region: 'global', currency: 'EUR', flag: '🇭🇷' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', region: 'global', currency: 'EUR', flag: '🇸🇰' },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', region: 'global', currency: 'EUR', flag: '🇸🇮' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски', region: 'global', currency: 'RSD', flag: '🇷🇸' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', region: 'global', currency: 'EUR', flag: '🇱🇹' },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu', region: 'global', currency: 'EUR', flag: '🇱🇻' },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti', region: 'global', currency: 'EUR', flag: '🇪🇪' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català', region: 'global', currency: 'EUR', flag: '🇪🇸' },
  { code: 'eu', name: 'Basque', nativeName: 'Euskara', region: 'global', currency: 'EUR', flag: '🇪🇸' },
  { code: 'gl', name: 'Galician', nativeName: 'Galego', region: 'global', currency: 'EUR', flag: '🇪🇸' },
  { code: 'is', name: 'Icelandic', nativeName: 'Íslenska', region: 'global', currency: 'ISK', flag: '🇮🇸' },
  { code: 'ga', name: 'Irish', nativeName: 'Gaeilge', region: 'global', currency: 'EUR', flag: '🇮🇪' },
  { code: 'cy', name: 'Welsh', nativeName: 'Cymraeg', region: 'global', currency: 'GBP', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },

  // African
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', region: 'global', currency: 'ZAR', flag: '🇿🇦' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', region: 'global', currency: 'KES', flag: '🇰🇪' },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', region: 'global', currency: 'ZAR', flag: '🇿🇦' },
  { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', region: 'global', currency: 'ZAR', flag: '🇿🇦' },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', region: 'global', currency: 'ETB', flag: '🇪🇹' },

  // Central Asian
  { code: 'ps', name: 'Pashto', nativeName: 'پښتو', region: 'global', currency: 'AFN', flag: '🇦🇫', rtl: true },
  { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', region: 'global', currency: 'UZS', flag: '🇺🇿' },
  { code: 'kk', name: 'Kazakh', nativeName: 'Қазақша', region: 'global', currency: 'KZT', flag: '🇰🇿' },
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycan', region: 'global', currency: 'AZN', flag: '🇦🇿' },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', region: 'global', currency: 'GEL', flag: '🇬🇪' },
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերdelays', region: 'global', currency: 'AMD', flag: '🇦🇲' },
  { code: 'mn', name: 'Mongolian', nativeName: 'Монгол', region: 'global', currency: 'MNT', flag: '🇲🇳' },
];

// ===============================
// COMBINED: ALL 81 LANGUAGES
// ===============================
export const allLanguages: Language[] = [...globalLanguages, ...indianLanguages];

// Get language by code
export const getLanguageByCode = (code: string): Language | undefined => {
  return allLanguages.find(lang => lang.code === code);
};

// Get languages by region
export const getLanguagesByRegion = (region: 'indian' | 'global'): Language[] => {
  return allLanguages.filter(lang => lang.region === region);
};

// Get RTL languages
export const getRtlLanguages = (): Language[] => {
  return allLanguages.filter(lang => lang.rtl);
};

// Check if language is RTL
export const isRtlLanguage = (code: string): boolean => {
  const lang = getLanguageByCode(code);
  return lang?.rtl ?? false;
};

export default allLanguages;
